(() => {

  // ==========================================================
  // CONFIG (fully dynamic from window.rewardBar, set by Liquid)
  // ==========================================================
  const CFG = window.rewardBar || {};
  if (!CFG.enabled) return;

  const GIFT_ENABLED    = !!(CFG.gift && CFG.gift.on && CFG.gift.variantId);
  const GIFT_VARIANT_ID = GIFT_ENABLED ? Number(CFG.gift.variantId) : null;
  const GIFT_THRESHOLD  = GIFT_ENABLED ? Number(CFG.gift.amount || 0) : Infinity;

  const SHIP_ENABLED   = !!(CFG.ship && CFG.ship.on);
  const SHIP_THRESHOLD = SHIP_ENABLED ? Number(CFG.ship.amount || 0) : Infinity;

  // Optional discount milestone (e.g. 10% off regular-priced items at RM 578). Display only: the discount itself is a
  // Shopify automatic discount. Present only for the currencies listed in the theme setting, and never together with the gift.
  // Measured on ELIGIBLE spend (product title without the exclude word, e.g. 'sale') BEFORE the discount, because the discount is
  // a line-level discount that lowers items_subtotal_price once it applies.
  const DISC           = CFG.disc || {};
  const DISC_THRESHOLD = Number(DISC.amount || 0);
  const DISC_ENDS_AT   = Number(DISC.endsAt || 0);
  const DISC_ENABLED   = !!(DISC.on && DISC_THRESHOLD > 0 && (!DISC_ENDS_AT || Date.now() < DISC_ENDS_AT * 1000));
  const DISC_LABEL     = DISC.label || '10% OFF';
  const DISC_EXCLUDE   = String(DISC.exclude || '').toLowerCase();
  const NAME_SHIP      = (CFG.names && CFG.names.ship) || 'FREE shipping';

  const CURRENCY  = CFG.currency || 'USD';
  const MESSAGES  = CFG.messages || {};
  const POPUPS    = CFG.popups || {};

  // Bar width basis: higher active threshold. Fall back to computed max.
  let MAX_AMOUNT = Number(CFG.maxAmount || 0);
  if (!MAX_AMOUNT) {
    MAX_AMOUNT = Math.max(
      isFinite(SHIP_THRESHOLD) ? SHIP_THRESHOLD : 0,
      isFinite(GIFT_THRESHOLD) ? GIFT_THRESHOLD : 0,
      DISC_ENABLED ? DISC_THRESHOLD : 0
    );
  }

  // Nothing active -> stop.
  if (!GIFT_ENABLED && !SHIP_ENABLED && !DISC_ENABLED) return;
  if (MAX_AMOUNT <= 0) return;

  // ==========================================================
  // CAPTURE ORIGINAL FETCH (hoisted before any function uses it)
  // ==========================================================
  const originalFetch = window.fetch;

  // ==========================================================
  // STATE
  // ==========================================================
  let processing         = false;
  let popupShown         = false;
  let removePopupShown   = false;
  let suppressWatcher    = false;
  let checkoutInProgress = false;

  // ==========================================================
  // MONEY FORMAT
  // ==========================================================
  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: CURRENCY,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  // ==========================================================
  // CART PAGE CHECK
  // ==========================================================
  function isCartPage() {
    return window.location.pathname === '/cart' ||
           window.location.pathname === '/cart/';
  }

  // ==========================================================
  // POPUP RELOCATION HELPERS
  // ----------------------------------------------------------
  // Both popups are relocated onto <body> once, so cart section
  // re-renders (cart:refresh) can't replace the popup node and
  // wipe it mid-appearance. Any duplicate the re-render re-inserts
  // is cleaned up.
  // ==========================================================
  function relocatePopup(selector) {
    const all = [...document.querySelectorAll(selector)];
    if (all.length === 0) return null;
    let keep = all.find(n => n.parentElement === document.body) || all[0];
    if (keep.parentElement !== document.body) document.body.appendChild(keep);
    all.forEach(n => { if (n !== keep) n.remove(); });
    return keep;
  }

  function getCartPopupEl()   { return relocatePopup('#mystery-gift-cart-popup'); }
  function getDrawerPopupEl() { return relocatePopup('#mystery-gift-popup'); }

  // ==========================================================
  // SHOW / HIDE POPUP
  // ==========================================================
  function showGiftPopup(title, message) {
    let popup;
    if (isCartPage()) {
      popup = getCartPopupEl();
      if (!popup) return;
      const t = popup.querySelector('#gift-cart-popup-title');
      const m = popup.querySelector('#gift-cart-popup-message');
      if (t) t.textContent = title;
      if (m) m.textContent = message;
    } else {
      popup = getDrawerPopupEl();
      if (!popup) return;
      const t = popup.querySelector('#gift-popup-title');
      const m = popup.querySelector('#gift-popup-message');
      if (t) t.textContent = title;
      if (m) m.textContent = message;
    }
    popup.classList.add('active');
  }

  function hideGiftPopup() {
    const popup = isCartPage() ? getCartPopupEl() : getDrawerPopupEl();
    if (!popup) return;
    popup.classList.remove('active');
    popupShown = false;
  }

  // Delegated close handlers (survive re-renders).
  document.addEventListener('click', function (event) {
    if (event.target.closest('#gift-popup-close')) { hideGiftPopup(); return; }
    if (event.target.closest('#gift-cart-popup-close')) { hideGiftPopup(); return; }
  });

  // ==========================================================
  // CART AJAX HELPERS
  // ==========================================================
  async function getCart() {
    suppressWatcher = true;
    try {
      const r = await originalFetch('/cart.js', {
        method: 'GET', headers: { 'Accept': 'application/json' }
      });
      if (!r.ok) throw new Error('Unable to get cart');
      return await r.json();
    } finally { suppressWatcher = false; }
  }

  async function addGift() {
    if (!GIFT_ENABLED) return false;
    suppressWatcher = true;
    try {
      const r = await originalFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: GIFT_VARIANT_ID, quantity: 1 }] })
      });
      if (!r.ok) { console.error('Add Gift Error:', await r.text()); return false; }
      return true;
    } finally { suppressWatcher = false; }
  }

  async function removeGift(lineKey) {
    suppressWatcher = true;
    try {
      const r = await originalFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
      if (!r.ok) { console.error('Remove Gift Error:', await r.text()); return false; }
      return true;
    } finally { suppressWatcher = false; }
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  // ==========================================================
  // SUBTOTAL (excludes the gift itself)
  // ==========================================================
  function getGiftLine(cart) {
    if (!GIFT_ENABLED) return null;
    let gift = null;
    cart.items.forEach(item => {
      if (Number(item.variant_id) === GIFT_VARIANT_ID) gift = item;
    });
    return gift;
  }

  function getQualifyingSubtotal(cart, gift) {
    let subtotal = Number(cart.items_subtotal_price || 0);
    if (gift) subtotal -= Number(gift.final_line_price || 0);
    return subtotal;
  }

  // Spend on discount-eligible lines, before the discount (same rule as the Liquid in snippets/reward-bar.liquid)
  function getEligibleSubtotal(cart, gift) {
    if (!DISC_ENABLED) return 0;
    let total = 0;
    cart.items.forEach(item => {
      if (gift && Number(item.variant_id) === GIFT_VARIANT_ID) return;
      const title = String(item.product_title || item.title || '').toLowerCase();
      if (DISC_EXCLUDE && title.indexOf(DISC_EXCLUDE) !== -1) return;
      total += Number(item.original_line_price || item.line_price || 0);
    });
    return total;
  }

  // ==========================================================
  // DISCOUNT-MARKET BAR (mirrors snippets/reward-bar.liquid exactly). Used only when DISC_ENABLED.
  // Free shipping is measured on the whole cart, the discount only on eligible (regular-priced) spend. Money is formatted the
  // way Shopify's `money` filter did on the server (same symbol, separators and currency suffix), so the text does not change
  // style when this script re-renders the bar.
  // ==========================================================
  const MONEY_PARTS = /^(.*?)(\d[\d.,'\s]*\d)(.*)$/.exec(String(CFG.moneySample || ''));

  function formatMoneyLikeStore(cents) {
    if (!MONEY_PARTS) return formatMoney(cents);
    const num = MONEY_PARTS[2];
    const dec = /([.,])(\d{2})$/.exec(num);
    const thousands = (num.slice(0, dec ? dec.index : num.length).match(/[.,'\s]/) || [''])[0];
    const parts = (Math.max(0, cents) / 100).toFixed(dec ? 2 : 0).split('.');
    const whole = thousands ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) : parts[0];
    return MONEY_PARTS[1] + whole + (dec ? dec[1] + parts[1] : '') + MONEY_PARTS[3];
  }

  function fillTemplate(template, values) {
    return Object.keys(values).reduce((text, key) => text.split('[' + key + ']').join(values[key]), template);
  }

  // The fill walks the goals in order, so it is never full while the discount is still ahead.
  function discountFill(subtotal, eligible) {
    if (!(SHIP_ENABLED && isFinite(SHIP_THRESHOLD))) return Math.min(100, (eligible / DISC_THRESHOLD) * 100);
    const shipPos = (SHIP_THRESHOLD / MAX_AMOUNT) * 100;
    if (subtotal < SHIP_THRESHOLD) return (subtotal / SHIP_THRESHOLD) * shipPos;
    const span = DISC_THRESHOLD - SHIP_THRESHOLD;
    const into = Math.min(span, Math.max(0, eligible - SHIP_THRESHOLD));
    return shipPos + (into / span) * (100 - shipPos);
  }

  function buildDiscountMessage(subtotal, eligible) {
    const hasShip = SHIP_ENABLED && isFinite(SHIP_THRESHOLD);
    const shipReached = hasShip && subtotal >= SHIP_THRESHOLD;
    const discReached = eligible >= DISC_THRESHOLD;
    const reached = [];
    if (shipReached) reached.push(NAME_SHIP);
    if (discReached) reached.push(DISC_LABEL);

    let next = null;
    if (hasShip && !shipReached) next = { type: 'shipping', remaining: SHIP_THRESHOLD - subtotal };
    else if (!discReached) next = { type: 'discount', remaining: DISC_THRESHOLD - eligible };

    if (!next) {
      return fillTemplate(MESSAGES.unlockedAll || "🎉 You've unlocked [reached]!", { reached: reached.join(' + ') });
    }
    const remaining = formatMoneyLikeStore(next.remaining);
    if (!reached.length) {
      if (next.type === 'shipping') return fillTemplate(MESSAGES.beforeShipping || 'Spend [amount] more to unlock FREE shipping!', { amount: remaining });
      return fillTemplate(MESSAGES.beforeDiscount || 'Spend [amount] more on regular-priced items to unlock [label]!', { amount: remaining, label: DISC_LABEL });
    }
    if (next.type === 'discount') {
      return fillTemplate(
        MESSAGES.unlockedNextDiscount || "🎉 You've unlocked [reached]! Spend [amount] more on regular-priced items to get [next].",
        { reached: reached.join(' + '), next: DISC_LABEL, amount: remaining }
      );
    }
    return fillTemplate(
      MESSAGES.unlockedNext || "🎉 You've unlocked [reached]! Spend [amount] more to get [next].",
      { reached: reached.join(' + '), next: NAME_SHIP, amount: remaining }
    );
  }

  // ==========================================================
  // BUILD MESSAGE (mirrors the Liquid logic exactly)
  // Lower threshold first; then the correct "done" variant.
  // ==========================================================
  function buildMessage(subtotal, eligible) {
    if (DISC_ENABLED) return buildDiscountMessage(subtotal, eligible);

    const hasShip = SHIP_ENABLED && isFinite(SHIP_THRESHOLD);
    const hasGift = GIFT_ENABLED && isFinite(GIFT_THRESHOLD);

    let nextType = '';
    let nextAmount = 0;

    if (hasShip && hasGift) {
      if (SHIP_THRESHOLD <= GIFT_THRESHOLD) {
        if (subtotal < SHIP_THRESHOLD) { nextType = 'shipping'; nextAmount = SHIP_THRESHOLD; }
        else if (subtotal < GIFT_THRESHOLD) { nextType = 'gift'; nextAmount = GIFT_THRESHOLD; }
      } else {
        if (subtotal < GIFT_THRESHOLD) { nextType = 'gift'; nextAmount = GIFT_THRESHOLD; }
        else if (subtotal < SHIP_THRESHOLD) { nextType = 'shipping'; nextAmount = SHIP_THRESHOLD; }
      }
    } else if (hasShip) {
      if (subtotal < SHIP_THRESHOLD) { nextType = 'shipping'; nextAmount = SHIP_THRESHOLD; }
    } else if (hasGift) {
      if (subtotal < GIFT_THRESHOLD) { nextType = 'gift'; nextAmount = GIFT_THRESHOLD; }
    }

    if (nextType === 'shipping') {
      const rem = Math.max(0, nextAmount - subtotal);
      return (MESSAGES.beforeShipping || 'Spend [amount] more to unlock FREE shipping!')
        .replace('[amount]', formatMoney(rem));
    }
    if (nextType === 'gift') {
      const rem = Math.max(0, nextAmount - subtotal);
      return (MESSAGES.beforeGift || 'Spend [amount] more to unlock your Mystery Gift!')
        .replace('[amount]', formatMoney(rem));
    }

    // All reached — pick the right done message.
    if (hasShip && hasGift) return MESSAGES.allDone || '🎉 You have unlocked everything!';
    if (hasShip)            return MESSAGES.shippingOnlyDone || '🎉 You have unlocked FREE shipping!';
    return MESSAGES.giftOnlyDone || '🎉 You have unlocked your FREE Mystery Gift!';
  }

  // ==========================================================
  // RENDER BAR (updates <reward-bar> markup)
  // ==========================================================
  function renderBar(subtotal, eligible) {
    const bars = document.querySelectorAll('[data-reward-bar]');
    if (!bars.length) return;

    const message = buildMessage(subtotal, eligible);
    const pct = DISC_ENABLED ? discountFill(subtotal, eligible) : Math.min(100, (subtotal / MAX_AMOUNT) * 100);

    bars.forEach(bar => {
      const msgEl  = bar.querySelector('[data-reward-message]');
      const fillEl = bar.querySelector('[data-reward-fill]');
      if (msgEl)  msgEl.innerHTML = message;
      if (fillEl) fillEl.style.width = pct + '%';
      // tick marks + labels exist only in discount markets (shipping is measured on the cart, the discount on eligible spend)
      bar.querySelectorAll('[data-reward-tick], [data-reward-step]').forEach(el => {
        const value = el.dataset.measure === 'eligible' ? eligible : subtotal;
        el.classList.toggle('is-reached', value >= Number(el.dataset.amount || Infinity));
      });
    });
  }

  // ==========================================================
  // SYNC (bar update + gift add/remove)
  // Gift removal is IMMEDIATE (never gated behind a popup), so a
  // gift can never stay stuck below threshold. Popups are shown
  // AFTER refreshCart() settles so the re-render can't wipe them.
  // ==========================================================
  async function syncGift() {
    if (processing) return;
    processing = true;

    try {
      const cart = await getCart();
      const gift = getGiftLine(cart);
      const subtotal = getQualifyingSubtotal(cart, gift);

      renderBar(subtotal, getEligibleSubtotal(cart, gift));

      if (!GIFT_ENABLED) return;

      // ---- QUALIFIES -> ensure gift present
      if (subtotal >= GIFT_THRESHOLD) {
        removePopupShown = false;

        if (!gift) {
          const added = await addGift();
          if (added) {
            refreshCart();
            setTimeout(() => {
              if (!popupShown) {
                popupShown = true;
                showGiftPopup(
                  POPUPS.addedTitle || 'Congratulations! 🎉',
                  POPUPS.addedMessage || "You've unlocked your FREE Mystery Gift!"
                );
              }
            }, 450);
          }
        }
      }
      // ---- DOES NOT QUALIFY -> remove gift immediately
      else {
        popupShown = false;

        if (gift) {
          const removed = await removeGift(gift.key);
          if (removed) {
            refreshCart();
            // Show the removal popup AFTER the re-render settles, so
            // the section re-render can't wipe it mid-appearance.
            if (!removePopupShown) {
              removePopupShown = true;
              setTimeout(() => {
                showGiftPopup(
                  POPUPS.removedTitle || 'Mystery Gift Removed',
                  (POPUPS.removedMessage || 'Your cart total is now below [amount], so your FREE Mystery Gift has been removed.')
                    .replace('[amount]', formatMoney(GIFT_THRESHOLD))
                );
              }, 450);
            }
          }
        } else {
          removePopupShown = false;
        }
      }
    } catch (error) {
      console.error('Reward Bar Error:', error);
    } finally {
      processing = false;
    }
  }

  // ==========================================================
  // CHECKOUT VALIDATION (verify → correct → re-verify)
  // ----------------------------------------------------------
  // On checkout click/submit: read the live cart, check the gift
  // is correct for the current subtotal. If wrong, fix it (add if
  // missing, remove if it shouldn't be there), then RE-READ to
  // confirm before navigation, so checkout never loads a stale or
  // wrong cart (avoids a write/navigate race condition).
  // ==========================================================
  async function validateGiftForCheckout() {
    if (!GIFT_ENABLED) return;

    // ---- Pass 1: check + correct
    let cart = await getCart();
    let gift = getGiftLine(cart);
    let subtotal = getQualifyingSubtotal(cart, gift);

    let corrected = false;

    if (subtotal >= GIFT_THRESHOLD) {
      if (!gift) { await addGift(); corrected = true; }
    } else {
      if (gift) { await removeGift(gift.key); corrected = true; }
    }

    // ---- Pass 2: re-verify only if we changed something
    if (corrected) {
      cart = await getCart();
      gift = getGiftLine(cart);
      subtotal = getQualifyingSubtotal(cart, gift);

      // Second-chance correction (rare — only if the first write
      // didn't land). Keeps the checkout cart 100% consistent.
      if (subtotal >= GIFT_THRESHOLD && !gift) {
        await addGift();
      } else if (subtotal < GIFT_THRESHOLD && gift) {
        await removeGift(gift.key);
      }
    }
  }

  // ==========================================================
  // CHECKOUT URL + TRIGGER
  // ==========================================================
  function getCheckoutUrl(triggerEl) {
    const href = triggerEl && triggerEl.getAttribute ? triggerEl.getAttribute('href') : null;
    if (href && href.includes('/checkout')) return href;
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root.replace(/\/$/, '') + '/checkout';
    }
    return '/checkout';
  }

  function getCheckoutTrigger(target) {
    if (!target || !target.closest) return null;
    return (
      target.closest('[name="checkout"]') ||
      target.closest('a[href*="/checkout"]') ||
      target.closest('button[href*="/checkout"]')
    );
  }

  // ---- CHECKOUT INTERCEPT — CLICK (capture phase)
  document.addEventListener('click', async function (event) {
    if (checkoutInProgress) return;
    const trigger = getCheckoutTrigger(event.target);
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    checkoutInProgress = true;

    const checkoutUrl = getCheckoutUrl(trigger);
    try { await validateGiftForCheckout(); }
    catch (e) { console.error('Checkout validation error:', e); }
    window.location.href = checkoutUrl;
  }, true);

  // ---- CHECKOUT INTERCEPT — FORM SUBMIT (capture phase)
  document.addEventListener('submit', async function (event) {
    if (checkoutInProgress) return;
    const form = event.target;
    if (!form || !form.action || !form.action.includes('/cart')) return;
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;

    event.preventDefault();
    checkoutInProgress = true;
    try { await validateGiftForCheckout(); }
    catch (e) { console.error('Checkout submit validation error:', e); }
    form.submit();
  }, true);

  // ==========================================================
  // WATCH CART AJAX
  // ==========================================================
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      if (suppressWatcher) return response;
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear')
      ) {
        setTimeout(syncGift, 100);
      }
    } catch (error) {
      console.error('Reward Bar watcher error:', error);
    }
    return response;
  };

  // ==========================================================
  // INIT
  // ==========================================================
  function init() { setTimeout(syncGift, 100); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();